import React, { Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import ReactGA from "react-ga";

import { Routes, Route, useLocation } from "react-router-dom";
import { Sidebar, Menu, MenuItem, useProSidebar } from "react-pro-sidebar";
import { isElectron } from "./components/Globals";
import { parseAppQuery, ROUTES } from "./lib/appRoutes";

import debug from "./lib/debug";
import AlertPopup from "./components/tools/AlertPopup";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GestureIcon from "@mui/icons-material/Gesture";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";

import { firebaseApp } from "./lib/firebaseApp";
import { getShell } from "./lib/apiKey";
import { installNativeFileOpen } from "./lib/nativeFileOpen";
import "./App.css";

import Header from "./components/Header";
import Footer from "./components/Footer";
import OfflineBanner from "./components/OfflineBanner";

const Login = React.lazy(() => import("./components/member/Login"));
const Register = React.lazy(() => import("./components/member/Register"));
const Table = React.lazy(() => import("./components/Table"));
const Playground = React.lazy(() => import("./components/tools/Playground"));
const Renderer = React.lazy(() => import("./components/visuals/Renderer"));
const RendererWrapper = React.lazy(() => import("./components/visuals/RendererWrapper"));
const NotFound = React.lazy(() => import("./components/NotFound"));
const Developer = React.lazy(() => import("./components/Developer"));
const Extensions = React.lazy(() => import("./components/tools/Extensions"));
const Download = React.lazy(() => import("./components/tools/Download"));
const DownloadWeb = React.lazy(() => import("./components/tools/DownloadWeb"));
const MolarMass = React.lazy(() => import("./components/tools/MolarMass"));
const Shop = React.lazy(() => import("./components/giftshop/Shop"));
const Cart = React.lazy(() => import("./components/giftshop/Cart"));
const ProductDetail = React.lazy(() => import("./components/giftshop/ProductDetail"));
const OrderHistory = React.lazy(() => import("./components/giftshop/OrderHistory"));
const OrderDetail = React.lazy(() => import("./components/giftshop/OrderDetail"));
const OrderSuccess = React.lazy(() => import("./components/giftshop/OrderSuccess"));
const AdminShop = React.lazy(() => import("./components/giftshop/AdminShop"));
const FileUploadForm = React.lazy(() =>
  import("./components/tools/FileUploadForm")
);
const Membership = React.lazy(() => import("./components/member/Membership"));
const MembershipElectron = React.lazy(() =>
  import("./components/member/MembershipElectron")
);
const SelfIonization = React.lazy(() =>
  import("./components/visuals/SelfIonization")
);
const QuantumOrbital = React.lazy(() =>
  import("./components/QuantumOrbital")
);
const Blog = React.lazy(() => import("./components/articles/Blog"));
const Article = React.lazy(() => import("./components/articles/Article"));
const Privacy = React.lazy(() => import("./components/Privacy"));
const CookiePolicy = React.lazy(() => import("./components/CookiePolicy"));
const CookieConsent = React.lazy(() => import("./components/CookieConsent"));
const Credits = React.lazy(() => import("./components/Credits"));
const License = React.lazy(() => import("./components/License"));
const ReactionBalancer = React.lazy(() => import("./components/tools/ReactionBalancer"));
const Docs = React.lazy(() => import("./components/Docs"));
const ApiKeys = React.lazy(() => import("./components/ApiKeys"));
const PeriodicTableExplorer = React.lazy(() => import("./components/tools/PeriodicTableExplorer"));
const CompareMode = React.lazy(() => import("./components/tools/CompareMode"));
const ReactionPathway = React.lazy(() => import("./components/tools/ReactionPathway"));
const StoichiometryCalc = React.lazy(() => import("./components/tools/StoichiometryCalc"));
const AcidBaseCalc = React.lazy(() => import("./components/tools/AcidBaseCalc"));
const QuizMode = React.lazy(() => import("./components/tools/QuizMode"));
const ElectronConfigBuilder = React.lazy(() => import("./components/tools/ElectronConfigBuilder"));
const Workbench = React.lazy(() => import("./components/workbench/Workbench"));
const Terms = React.lazy(() => import("./components/Terms"));
const Support = React.lazy(() => import("./components/Support"));
const Pricing = React.lazy(() => import("./components/Pricing"));
const EmbeddedLicenses = React.lazy(() => import("./components/embedded/EmbeddedLicenses"));
const MoleculePage = React.lazy(() => import("./components/MoleculePage"));
const MoleculeLibrary = React.lazy(() =>
  import("./components/MoleculePage").then((m) => ({ default: m.MoleculeLibrary }))
);
const ToolsIndex = React.lazy(() => import("./components/ToolsIndex"));
const DensityTool = React.lazy(() => import("./components/density/DensityTool"));
const CreditNudge = React.lazy(() => import("./components/CreditNudge"));

const TRACKING_ID = "G-15Q6HNH1D8"; // Google Analytics Tracking ID

// Firebase apps initialised in lib/firebaseApp.ts. Re-exporting so
// legacy imports (`import { firebaseApp } from "./App"`) keep working.
export { firebaseApp };

export default function App() {
  /*
  This is a component function in JSX that contains the HTML markup
  that represent each graphical element on the webpage;
  This specific function handles React's client-side routing feature

  Parameters
  ----------
  None

  Returns
  -------
  DOM File
    A HTML markup that contains graphical elements
  */

  const navigate = useNavigate();
  const location = useLocation();
  const appQuery = parseAppQuery(location.search);
  const { collapseSidebar } = useProSidebar();

  useEffect(() => {
    ReactGA.initialize(TRACKING_ID);
    ReactGA.pageview(window.location.pathname + window.location.search);
    debug.log("info", "App mounted — debug logging active (set window.__EV_DEBUG = false to disable)");
  }, []);

  useEffect(() => {
    debug.log("route", `${location.pathname}${location.search}`);
  }, [location]);

  // Mac apps hand files opened from Finder / File > Open to the
  // Workbench through window.__nativeOpenFile (lib/nativeFileOpen.ts).
  useEffect(() => {
    installNativeFileOpen(() => {
      if (window.location.pathname !== ROUTES.workbench) navigate(ROUTES.workbench);
    });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    import("firebase/analytics").then(({ getAnalytics, isSupported }) => {
      if (cancelled) return;
      isSupported().then((ok) => {
        if (ok && !cancelled) getAnalytics(firebaseApp);
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isElectron()) {
      console.log("Electron is running");
      collapseSidebar();
    }
  }, [collapseSidebar]);

  // Light-theme switch for the Electronify shell only — sets a body
  // class that index.css uses to invert the dark UI.
  useEffect(() => {
    const cls = "shell-electronify-light";
    if (getShell() === "electronify") {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
  }, []);

  const Fallback = () => {
    return (
      <div
        className="p-10 bg-ev-bg flex flex-col items-center justify-center"
        style={{ minHeight: "100vh", width: "-webkit-fill-available" }}
      >
        <div className="ev-progress is-indeterminate w-40" aria-hidden>
          <span />
        </div>
        <p className="ev-eyebrow mt-5" role="status">
          Loading
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-ev-bg text-ev-fg antialiased">
      {!appQuery.fullscreen && <Header />}

      <div id="main" tabIndex={-1} className={`outline-none ${isElectron() ? "flex flex-row" : ""}`}>
        <AlertPopup />
        {isElectron() && (
          <Sidebar style={{ height: "inherit" }}>
            <Menu>
              <MenuItem
                icon={<MenuOutlinedIcon />}
                onClick={() => {
                  collapseSidebar();
                }}
                style={{ textAlign: "center" }}
              ></MenuItem>

              <MenuItem
                icon={<HomeOutlinedIcon />}
                onClick={() => {
                  collapseSidebar(true);
                  navigate(ROUTES.home);
                }}
              >
                Home
              </MenuItem>

              <MenuItem
                icon={<GestureIcon />}
                onClick={() => {
                  collapseSidebar(true);
                  navigate(ROUTES.renderer);
                }}
              >
                Spotlight
              </MenuItem>

              <MenuItem
                icon={<LibraryBooksIcon />}
                onClick={() => {
                  collapseSidebar(true);
                  navigate("/blog");
                }}
              >
                Articles
              </MenuItem>

              <MenuItem
                icon={<InsertEmoticonIcon />}
                onClick={() => {
                  collapseSidebar(true);
                  navigate(ROUTES.login);
                }}
              >
                Member
              </MenuItem>
            </Menu>
          </Sidebar>
        )}

        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path={ROUTES.home} element={<Table />} />
            <Route path={ROUTES.renderer} element={<Renderer />} />
            <Route path={ROUTES.rendererWrapper} element={<RendererWrapper />} />
            <Route path={ROUTES.dev} element={<Developer />} />
            <Route path={ROUTES.login} element={<Login />} />
            <Route path={ROUTES.register} element={<Register />} />
            <Route path={ROUTES.playground} element={<Playground />} />
            <Route path={ROUTES.extensions} element={<Extensions />} />
            <Route path={ROUTES.download} element={<Download />} />
            <Route path={ROUTES.downloadWeb} element={<DownloadWeb />} />
            <Route path={ROUTES.molarMass} element={<MolarMass />} />
            <Route path={ROUTES.fileUpload} element={<FileUploadForm />} />
            <Route path={ROUTES.shop} element={<Shop />} />
            <Route path={ROUTES.shopProduct} element={<ProductDetail />} />
            <Route path={ROUTES.cart} element={<Cart />} />
            <Route path={ROUTES.shopOrders} element={<OrderHistory />} />
            <Route path={ROUTES.shopOrderSuccess} element={<OrderSuccess />} />
            <Route path={ROUTES.shopOrderDetail} element={<OrderDetail />} />
            <Route path={ROUTES.adminShop} element={<AdminShop />} />
            <Route path={ROUTES.membership} element={<Membership />} />
            <Route
              path={ROUTES.membershipElectron}
              element={<MembershipElectron />}
            />
            <Route path={ROUTES.selfIonization} element={<SelfIonization />} />
            <Route path={ROUTES.quantumOrbitals} element={<QuantumOrbital />} />
            <Route path={ROUTES.blog} element={<Blog />} />
            <Route path={ROUTES.article} element={<Article />} />
            <Route path={ROUTES.privacy} element={<Privacy />} />
            <Route path={ROUTES.cookies} element={<CookiePolicy />} />
            <Route path={ROUTES.credits} element={<Credits />} />
            <Route path={ROUTES.license} element={<License />} />
            <Route path={ROUTES.balance} element={<ReactionBalancer />} />
            <Route path={ROUTES.docs} element={<Docs />} />
            <Route path={ROUTES.apiKeys} element={<ApiKeys />} />
            <Route path={ROUTES.apiPricing} element={<ApiKeys />} />
            <Route path={ROUTES.periodicTable} element={<PeriodicTableExplorer />} />
            <Route path={ROUTES.compare} element={<CompareMode />} />
            <Route path={ROUTES.reaction} element={<ReactionPathway />} />
            <Route path={ROUTES.stoichiometry} element={<StoichiometryCalc />} />
            <Route path={ROUTES.acidBase} element={<AcidBaseCalc />} />
            <Route path={ROUTES.quiz} element={<QuizMode />} />
            <Route path={ROUTES.configBuilder} element={<ElectronConfigBuilder />} />
            <Route path={ROUTES.workbench} element={<Workbench />} />
            <Route path={ROUTES.terms} element={<Terms />} />
            <Route path={ROUTES.support} element={<Support />} />
            <Route path={ROUTES.tools} element={<ToolsIndex />} />
            <Route path={ROUTES.pricing} element={<Pricing />} />
            <Route path="/embedded/licenses" element={<EmbeddedLicenses />} />
            <Route path={ROUTES.molecules} element={<MoleculeLibrary />} />
            <Route path={ROUTES.molecule} element={<MoleculePage />} />
            <Route path={ROUTES.density} element={<DensityTool />} />
            <Route path={ROUTES.densityResult} element={<DensityTool />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!appQuery.fullscreen && <Footer />}
      <OfflineBanner />
      <Suspense fallback={null}>
        <CookieConsent />
        <CreditNudge />
      </Suspense>
    </div>
  );
}
